import React, { type FC, useMemo } from 'react';

import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';

import type { RendererExtensionHandlers } from '@confluence/fabric-extension-lib/entry-points/extensionTypes.ts';
import { MustPreloadLoadableNames, mustPreloadWhenRenderingSPA } from '@confluence/loadable';
import {
	createNestedAdfDocument,
	type NestedAdfRendererWrapper,
	type NestedAdfRendererWrapperProps,
} from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';

export type NestedAdfSSRRendererWrapperProviderProps = {
	appearance?: string;
	Renderer: FC<any>;
	getExtensionHandlers: (options: any) => RendererExtensionHandlers;
	showTemplateInputInPreview?: boolean;
	showTemplateVariablesInPreview?: boolean;
	children: (RendererWrapper: NestedAdfRendererWrapper) => React.ReactNode;
};

export const NestedAdfSSRRendererWrapperProvider: FC<NestedAdfSSRRendererWrapperProviderProps> = ({
	appearance,
	Renderer,
	getExtensionHandlers,
	showTemplateInputInPreview,
	showTemplateVariablesInPreview,
	children,
}) => {
	mustPreloadWhenRenderingSPA([
		MustPreloadLoadableNames.ClassicRendererLoader,
		MustPreloadLoadableNames.ClassicEditorContextProviderLoader,
	]);

	const RendererWrapper = useMemo(
		() =>
			({
				adf,
				targetContentId,
				targetSpaceKey,
				mediaToken,
				headingIdPrefix,
			}: NestedAdfRendererWrapperProps) => {
				const document = createNestedAdfDocument(adf);

				const extensionHandlers = getExtensionHandlers({
					adf: document,
					contentId: targetContentId,
					spaceKey: targetSpaceKey,
					appearance,
					showTemplateInputInPreview,
					showTemplateVariablesInPreview,
				});

				return (
					<RendererActionsContext>
						<Renderer
							contentId={targetContentId}
							document={document}
							hasInlineComments={false}
							showTemplateInputInPreview={showTemplateInputInPreview}
							showTemplateVariablesInPreview={showTemplateVariablesInPreview}
							extensionHandlers={extensionHandlers}
							appearance={appearance || 'default'}
							spaceKey={targetSpaceKey}
							mediaToken={mediaToken}
							headingIdPrefix={headingIdPrefix}
						/>
					</RendererActionsContext>
				);
			},
		[
			Renderer,
			appearance,
			getExtensionHandlers,
			showTemplateInputInPreview,
			showTemplateVariablesInPreview,
		],
	);

	return <>{children(RendererWrapper)}</>;
};
